/**
 * F-072 — Lightweight i18n core.
 *
 * Detects the user's preferred locale from `navigator.languages`, applies
 * the matching catalog, and exposes `t(key, vars?)` + `formatNumber()` to
 * the rest of the app. Falls back to English when no entry matches.
 *
 * No external dep — the catalog (~36 keys × 10 locales) is small enough
 * for a hand-rolled module.
 */

import { CATALOGS } from "./locales.ts";

export type Locale =
  | "ko"
  | "en"
  | "ja"
  | "zh-CN"
  | "zh-TW"
  | "es"
  | "fr"
  | "de"
  | "pt-BR"
  | "ru";

export const SUPPORTED_LOCALES: ReadonlyArray<Locale> = [
  "ko",
  "en",
  "ja",
  "zh-CN",
  "zh-TW",
  "es",
  "fr",
  "de",
  "pt-BR",
  "ru",
];

export const DEFAULT_LOCALE: Locale = "en";

/**
 * BCP-47 base-tag → Locale fallback map. Used when an exact match misses
 * but the base language is supported (e.g. `pt-PT` → `pt-BR`, `zh-Hans`
 * → `zh-CN`).
 */
const BASE_FALLBACKS: Record<string, Locale> = {
  ko: "ko",
  en: "en",
  ja: "ja",
  zh: "zh-CN",
  "zh-hans": "zh-CN",
  "zh-hant": "zh-TW",
  es: "es",
  fr: "fr",
  de: "de",
  pt: "pt-BR",
  ru: "ru",
};

const SUPPORTED_SET: Set<string> = new Set(SUPPORTED_LOCALES);

function normalizeTag(tag: string): string {
  return tag.toLowerCase().replace(/_/g, "-");
}

/**
 * Walk the candidate list and return the first locale we can support.
 *
 * Resolution order per candidate:
 *   1. Exact case-insensitive match against SUPPORTED_LOCALES.
 *   2. Subtag-stripping: drop trailing subtags one by one and try each.
 *   3. Script/region fallback table (e.g. `zh-hant` → `zh-TW`).
 *
 * Returns DEFAULT_LOCALE when nothing matches.
 */
export function detectLocale(
  candidates: ReadonlyArray<string> | undefined = undefined,
): Locale {
  const list =
    candidates ??
    (typeof navigator !== "undefined"
      ? (navigator.languages && navigator.languages.length > 0
          ? navigator.languages
          : navigator.language
            ? [navigator.language]
            : [])
      : []);

  const supportedLower = new Map<string, Locale>();
  for (const loc of SUPPORTED_LOCALES) supportedLower.set(loc.toLowerCase(), loc);

  for (const raw of list) {
    if (!raw) continue;
    const tag = normalizeTag(raw);
    // 1. Exact case-insensitive match.
    const exact = supportedLower.get(tag);
    if (exact) return exact;
    // 2. Strip subtags from the right (zh-Hant-TW → zh-Hant → zh) and
    //    consult both supported list and BASE_FALLBACKS at each level.
    //    Length includes the full tag so script-only fallbacks (e.g.
    //    "zh-hant" → "zh-TW") get a chance before we collapse to "zh".
    const parts = tag.split("-");
    for (let i = parts.length; i >= 1; i -= 1) {
      const sub = parts.slice(0, i).join("-");
      const m = supportedLower.get(sub);
      if (m) return m;
      const fb = BASE_FALLBACKS[sub];
      if (fb && SUPPORTED_SET.has(fb)) return fb;
    }
  }

  return DEFAULT_LOCALE;
}

let activeLocale: Locale = DEFAULT_LOCALE;

export function getLocale(): Locale {
  return activeLocale;
}

export function setLocale(locale: Locale): void {
  activeLocale = SUPPORTED_SET.has(locale) ? locale : DEFAULT_LOCALE;
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = activeLocale;
  }
}

function interpolate(
  template: string,
  vars: Record<string, string | number> | undefined,
): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const v = vars[name];
    return v === undefined ? `{${name}}` : String(v);
  });
}

/**
 * Look up a translation key against the active locale, falling back to
 * English then to the key itself if nothing matches. Supports `{var}`
 * style interpolation.
 */
export function t(
  key: string,
  vars?: Record<string, string | number>,
): string {
  const active = CATALOGS[activeLocale];
  const fromActive = active ? active[key] : undefined;
  if (fromActive !== undefined) return interpolate(fromActive, vars);
  const fromEn = CATALOGS[DEFAULT_LOCALE]?.[key];
  if (fromEn !== undefined) return interpolate(fromEn, vars);
  return key;
}

/** Format a number with the active locale's grouping separator. */
export function formatNumber(value: number): string {
  try {
    return new Intl.NumberFormat(activeLocale).format(value);
  } catch {
    return String(value);
  }
}

export const __testing = { BASE_FALLBACKS, normalizeTag };
